// F1 Data Viewer Page - Query Builder Interface
import React, { useState } from 'react';
import F1PageTemplate from '../../components/f1/F1PageTemplate';
import { useSiblingData } from '../../hooks/useSiblingData';

const F1DataViewerPage = () => {
  const [selectedFile, setSelectedFile] = useState('');
  const [filters, setFilters] = useState([]);
  const [results, setResults] = useState(null);
  const [resolveIds, setResolveIds] = useState(true);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [orderBy, setOrderBy] = useState({ column: '', direction: 'ASC' });
  const [limit, setLimit] = useState(100);
  const [groupBy, setGroupBy] = useState({ column: '', aggregate: 'none' });
  
  // Available data files
  const availableFiles = [
    { id: 'drivers.csv', name: 'Drivers' },
    { id: 'races.csv', name: 'Races' },
    { id: 'constructors.csv', name: 'Teams/Constructors' },
    { id: 'circuits.csv', name: 'Circuits' },
    { id: 'seasons.csv', name: 'Seasons' },
    { id: 'pit_stops.csv', name: 'Pit Stops' },
    { id: 'qualifying.csv', name: 'Qualifying' },
    { id: 'results.csv', name: 'Race Results' },
    { id: 'driver_standings.csv', name: 'Driver Standings' },
    { id: 'constructor_standings.csv', name: 'Constructor Standings' },
    { id: 'lap_times.csv', name: 'Lap Times' },
    { id: 'sprint_results.csv', name: 'Sprint Results' },
    { id: 'status.csv', name: 'Status' }
  ];

  // Load selected file data
  const { data: fileData, loading, error } = useSiblingData(selectedFile);

  // Load reference data for ID resolution
  const { data: driversData } = useSiblingData(resolveIds ? 'drivers.csv' : '');
  const { data: constructorsData } = useSiblingData(resolveIds ? 'constructors.csv' : '');
  const { data: circuitsData } = useSiblingData(resolveIds ? 'circuits.csv' : '');
  const { data: racesData } = useSiblingData(resolveIds ? 'races.csv' : '');

  // Create lookup maps for ID resolution
  const driverMap = driversData?.reduce((acc, driver) => {
    acc[driver.driverId] = `${driver.forename} ${driver.surname}`;
    return acc;
  }, {}) || {};

  const constructorMap = constructorsData?.reduce((acc, team) => {
    acc[team.constructorId] = team.name;
    return acc;
  }, {}) || {};

  const circuitMap = circuitsData?.reduce((acc, circuit) => {
    acc[circuit.circuitId] = circuit.name;
    return acc;
  }, {}) || {};

  const raceMap = racesData?.reduce((acc, race) => {
    acc[race.raceId] = race.name;
    return acc;
  }, {}) || {};

  // Function to resolve IDs to human-readable names
  const resolveId = (columnName, value) => {
    if (!resolveIds || value === null || value === undefined || value === '') {
      return value;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) return value;

    switch (columnName) {
      case 'driverId':
        return driverMap[numValue] || value;
      case 'constructorId':
        return constructorMap[numValue] || value;
      case 'circuitId':
        return circuitMap[numValue] || value;
      case 'raceId':
        return raceMap[numValue] || value;
      default:
        return value;
    }
  };

  // Add a new filter condition
  const addFilter = () => {
    setFilters([...filters, { field: '', operator: 'equals', value: '', logic: 'AND' }]);
  };

  // Update a filter condition
  const updateFilter = (index, field, value) => {
    const newFilters = [...filters];
    newFilters[index][field] = value;
    setFilters(newFilters);
  };

  // Remove a filter condition
  const removeFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  // Execute query with filters
  const executeQuery = () => {
    if (!fileData || fileData.length === 0) return;

    let filteredData = [...fileData];

    // Apply each filter (WHERE clause) with AND/OR logic
    if (filters.length > 0) {
      filteredData = filteredData.filter(row => {
        const filterResults = filters.map(filter => {
          if (!filter.field) return true;
          
          const rowValue = String(row[filter.field] || '').toLowerCase();
          const filterValue = String(filter.value || '').toLowerCase();

          switch (filter.operator) {
            case 'equals':
              return rowValue === filterValue;
            case 'notEquals':
              return rowValue !== filterValue;
            case 'contains':
              return rowValue.includes(filterValue);
            case 'notContains':
              return !rowValue.includes(filterValue);
            case 'startsWith':
              return rowValue.startsWith(filterValue);
            case 'endsWith':
              return rowValue.endsWith(filterValue);
            case 'greaterThan':
              return parseFloat(rowValue) > parseFloat(filterValue);
            case 'lessThan':
              return parseFloat(rowValue) < parseFloat(filterValue);
            case 'greaterThanOrEqual':
              return parseFloat(rowValue) >= parseFloat(filterValue);
            case 'lessThanOrEqual':
              return parseFloat(rowValue) <= parseFloat(filterValue);
            case 'isNull':
              return rowValue === '' || rowValue === 'null';
            case 'isNotNull':
              return rowValue !== '' && rowValue !== 'null';
            default:
              return true;
          }
        });

        // Apply AND/OR logic
        let result = filterResults[0];
        for (let i = 1; i < filters.length; i++) {
          const logic = filters[i].logic || 'AND';
          if (logic === 'AND') {
            result = result && filterResults[i];
          } else {
            result = result || filterResults[i];
          }
        }
        return result;
      });
    }

    // Apply ORDER BY
    if (orderBy.column) {
      filteredData.sort((a, b) => {
        const aVal = a[orderBy.column];
        const bVal = b[orderBy.column];
        
        if (aVal === bVal) return 0;
        
        const comparison = aVal < bVal ? -1 : 1;
        return orderBy.direction === 'ASC' ? comparison : -comparison;
      });
    }

    // Apply GROUP BY with aggregation
    if (groupBy.column && groupBy.aggregate !== 'none') {
      const groups = {};
      filteredData.forEach(row => {
        const key = row[groupBy.column];
        if (!groups[key]) {
          groups[key] = { 
            [groupBy.column]: key, 
            _count: 0, 
            _values: [] 
          };
        }
        groups[key]._count++;
        groups[key]._values.push(row);
      });

      // Apply aggregation
      const aggregatedData = Object.values(groups).map(group => {
        const values = group._values.map(v => parseFloat(v[groupBy.column]) || 0);
        
        switch (groupBy.aggregate) {
          case 'count':
            return { [groupBy.column]: group[groupBy.column], count: group._count };
          case 'sum':
            return { [groupBy.column]: group[groupBy.column], sum: values.reduce((a, b) => a + b, 0) };
          case 'avg':
            return { [groupBy.column]: group[groupBy.column], avg: values.reduce((a, b) => a + b, 0) / values.length };
          case 'min':
            return { [groupBy.column]: group[groupBy.column], min: Math.min(...values) };
          case 'max':
            return { [groupBy.column]: group[groupBy.column], max: Math.max(...values) };
          default:
            return group;
        }
      });

      filteredData = aggregatedData;
    }

    // Apply LIMIT
    if (limit > 0) {
      filteredData = filteredData.slice(0, limit);
    }

    setResults(filteredData);
  };

  // Get available columns from loaded data
  const getColumns = () => {
    if (!fileData || fileData.length === 0) return [];
    return Object.keys(fileData[0]);
  };

  const columns = getColumns();

  // Get display columns (either selected or all)
  const displayColumns = selectedColumns.length > 0 ? selectedColumns : columns;

  return (
    <F1PageTemplate
      title="F1 Data Viewer"
      subtitle="Query and explore Formula 1 racing data"
      summary="Build custom queries to filter and analyze F1 data from multiple sources including drivers, races, teams, circuits, and more."
    >
      <div className="space-y-8">
        {/* Data Source Selection */}
        <div className="theme-bg rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold theme-primary mb-4">Select Data Source</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {availableFiles.map(file => (
              <button
                key={file.id}
                onClick={() => {
                  setSelectedFile(file.id);
                  setResults(null);
                  setFilters([]);
                  setSelectedColumns([]);
                  setOrderBy({ column: '', direction: 'ASC' });
                  setLimit(100);
                  setGroupBy({ column: '', aggregate: 'none' });
                }}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedFile === file.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="font-medium theme-primary">{file.name}</div>
                <div className="text-xs theme-secondary mt-1">{file.id}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Query Builder */}
        {selectedFile && (
          <div className="theme-bg rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold theme-primary">Query Builder</h2>
              <label className="flex items-center gap-2 theme-secondary">
                <input
                  type="checkbox"
                  checked={resolveIds}
                  onChange={(e) => setResolveIds(e.target.checked)}
                  className="w-4 h-4"
                />
                Resolve IDs to names
              </label>
            </div>
            
            {/* SELECT - Column Selection */}
            <div className="mb-6 p-4 border rounded-lg theme-border-secondary">
              <h3 className="font-medium theme-secondary mb-3">SELECT Columns</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {columns.map(col => (
                  <label key={col} className="flex items-center gap-2 text-sm theme-secondary">
                    <input
                      type="checkbox"
                      checked={selectedColumns.includes(col)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedColumns([...selectedColumns, col]);
                        } else {
                          setSelectedColumns(selectedColumns.filter(c => c !== col));
                        }
                      }}
                      className="w-4 h-4"
                    />
                    {col}
                  </label>
                ))}
              </div>
              <button
                onClick={() => setSelectedColumns([])}
                className="mt-3 text-sm text-blue-500 hover:text-blue-600"
              >
                Clear Selection (Show All)
              </button>
            </div>

            {/* ORDER BY */}
            <div className="mb-6 p-4 border rounded-lg theme-border-secondary">
              <h3 className="font-medium theme-secondary mb-3">ORDER BY</h3>
              <div className="flex gap-4 items-center">
                <select
                  value={orderBy.column}
                  onChange={(e) => setOrderBy({ ...orderBy, column: e.target.value })}
                  className="flex-1 p-2 border rounded-lg theme-bg theme-border-secondary theme-primary"
                >
                  <option value="">Select column to sort...</option>
                  {columns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
                <select
                  value={orderBy.direction}
                  onChange={(e) => setOrderBy({ ...orderBy, direction: e.target.value })}
                  className="w-24 p-2 border rounded-lg theme-bg theme-border-secondary theme-primary"
                >
                  <option value="ASC">ASC</option>
                  <option value="DESC">DESC</option>
                </select>
              </div>
            </div>

            {/* LIMIT */}
            <div className="mb-6 p-4 border rounded-lg theme-border-secondary">
              <h3 className="font-medium theme-secondary mb-3">LIMIT</h3>
              <div className="flex gap-4 items-center">
                <input
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value) || 0)}
                  min="0"
                  placeholder="Max results (0 = unlimited)"
                  className="flex-1 p-2 border rounded-lg theme-bg theme-border-secondary theme-primary"
                />
                <span className="text-sm theme-secondary">rows</span>
              </div>
            </div>

            {/* GROUP BY */}
            <div className="mb-6 p-4 border rounded-lg theme-border-secondary">
              <h3 className="font-medium theme-secondary mb-3">GROUP BY with Aggregation</h3>
              <div className="flex gap-4 items-center">
                <select
                  value={groupBy.column}
                  onChange={(e) => setGroupBy({ ...groupBy, column: e.target.value })}
                  className="flex-1 p-2 border rounded-lg theme-bg theme-border-secondary theme-primary"
                >
                  <option value="">Select column to group...</option>
                  {columns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
                <select
                  value={groupBy.aggregate}
                  onChange={(e) => setGroupBy({ ...groupBy, aggregate: e.target.value })}
                  className="w-32 p-2 border rounded-lg theme-bg theme-border-secondary theme-primary"
                >
                  <option value="none">No Aggregation</option>
                  <option value="count">COUNT</option>
                  <option value="sum">SUM</option>
                  <option value="avg">AVG</option>
                  <option value="min">MIN</option>
                  <option value="max">MAX</option>
                </select>
              </div>
            </div>

            {/* WHERE - Filters */}
            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center">
                <h3 className="font-medium theme-secondary">WHERE Filters</h3>
                <button
                  onClick={addFilter}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  + Add Filter
                </button>
              </div>

              {filters.map((filter, index) => (
                <div key={index} className="flex gap-4 items-center">
                  {index > 0 && (
                    <select
                      value={filter.logic}
                      onChange={(e) => updateFilter(index, 'logic', e.target.value)}
                      className="w-16 p-2 border rounded-lg theme-bg theme-border-secondary theme-primary text-sm font-medium"
                    >
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </select>
                  )}
                  
                  <select
                    value={filter.field}
                    onChange={(e) => updateFilter(index, 'field', e.target.value)}
                    className="flex-1 p-2 border rounded-lg theme-bg theme-border-secondary theme-primary"
                  >
                    <option value="">Select field...</option>
                    {columns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>

                  <select
                    value={filter.operator}
                    onChange={(e) => updateFilter(index, 'operator', e.target.value)}
                    className="w-40 p-2 border rounded-lg theme-bg theme-border-secondary theme-primary"
                  >
                    <option value="equals">= Equals</option>
                    <option value="notEquals">≠ Not Equals</option>
                    <option value="contains">Contains</option>
                    <option value="notContains">Not Contains</option>
                    <option value="startsWith">Starts With</option>
                    <option value="endsWith">Ends With</option>
                    <option value="greaterThan">&gt; Greater Than</option>
                    <option value="lessThan">&lt; Less Than</option>
                    <option value="greaterThanOrEqual">≥ Greater/Equal</option>
                    <option value="lessThanOrEqual">≤ Less/Equal</option>
                    <option value="isNull">Is Null</option>
                    <option value="isNotNull">Is Not Null</option>
                  </select>

                  <input
                    type="text"
                    value={filter.value}
                    onChange={(e) => updateFilter(index, 'value', e.target.value)}
                    placeholder="Value"
                    className="flex-1 p-2 border rounded-lg theme-bg theme-border-secondary theme-primary"
                    disabled={filter.operator === 'isNull' || filter.operator === 'isNotNull'}
                  />

                  <button
                    onClick={() => removeFilter(index)}
                    className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {/* Execute Button */}
            <button
              onClick={executeQuery}
              disabled={loading || !fileData}
              className="w-full p-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Execute Query'}
            </button>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="theme-bg rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold theme-primary">Query Results</h2>
              <div className="text-sm theme-secondary">
                {results.length} records found
              </div>
            </div>

            {results.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b theme-border-secondary">
                      {displayColumns.map(col => (
                        <th 
                          key={col} 
                          className="p-3 text-left theme-primary font-medium cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                          onClick={() => setOrderBy({ 
                            column: col, 
                            direction: orderBy.column === col && orderBy.direction === 'ASC' ? 'DESC' : 'ASC' 
                          })}
                        >
                          {col}
                          {orderBy.column === col && (
                            <span className="ml-2">{orderBy.direction === 'ASC' ? '↑' : '↓'}</span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-b theme-border-secondary hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        {displayColumns.map(col => (
                          <td key={col} className="p-3 theme-secondary text-sm">
                            {row[col] !== null && row[col] !== undefined 
                              ? String(resolveId(col, row[col])) 
                              : '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 theme-secondary">
                No results found matching your query
              </div>
            )}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="text-red-600 dark:text-red-400 font-medium">Error loading data</div>
            <div className="text-red-500 dark:text-red-300 text-sm mt-1">{error}</div>
          </div>
        )}
      </div>
    </F1PageTemplate>
  );
};

export default F1DataViewerPage;
